import { createClient } from '@supabase/supabase-js';

const runtimeConfig = typeof window !== 'undefined'
  ? (window.__STONKS_RUNTIME_CONFIG__ || window._env_ || {})
  : {};

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || runtimeConfig.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || runtimeConfig.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

export function isSupabaseReady() {
  return !!supabase;
}

// Get or create an anonymous session ID stored in localStorage
export function getSessionId() {
  const key = 'stonks_session_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(key, id);
  }
  return id;
}

// ── Transaction Sheets ────────────────────────────────────────────────────────

export async function listTransactionSheets() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('stonks_transaction_sheets')
    .select('id, name, updated_at')
    .order('updated_at', { ascending: false });
  if (error) { console.error(error); return []; }
  return data || [];
}

export async function createTransactionSheet(name = 'New Portfolio') {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('stonks_transaction_sheets')
    .insert({ name, session_id: getSessionId() })
    .select()
    .single();
  if (error) { console.error(error); return null; }
  return data;
}

export async function getTransactionSheet(id) {
  if (!supabase || !id || id === 'local') return null;
  const { data, error } = await supabase
    .from('stonks_transaction_sheets')
    .select('id, name, updated_at')
    .eq('id', id)
    .single();
  if (error) { console.error(error); return null; }
  return data;
}

export async function renameTransactionSheet(id, name) {
  if (!supabase) return;
  await supabase
    .from('stonks_transaction_sheets')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', id);
}

export async function deleteTransactionSheet(id) {
  if (!supabase) return;
  await supabase.from('stonks_transaction_sheets').delete().eq('id', id);
}

export async function getTransactionRows(sheetId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('stonks_transactions')
    .select('*')
    .eq('sheet_id', sheetId)
    .order('position_order', { ascending: true });
  if (error) { console.error(error); return []; }
  return data || [];
}

export async function saveTransactionRows(sheetId, rows) {
  if (!supabase) return;
  // Upsert all rows; delete rows not in the current set
  await supabase.from('stonks_transactions').delete().eq('sheet_id', sheetId);
  if (rows.length === 0) return;
  const toInsert = rows.map((r, i) => ({
    sheet_id: sheetId,
    ticker: (r.ticker || '').toUpperCase().trim(),
    type: r.type || 'purchase',
    date: r.date || null,
    amount: r.amount || null,
    position_order: i,
  }));
  const { error } = await supabase.from('stonks_transactions').insert(toInsert);
  if (error) console.error(error);
  // Bump updated_at
  await supabase
    .from('stonks_transaction_sheets')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', sheetId);
}

// ── Backtest Sheets ───────────────────────────────────────────────────────────

export async function listBacktestSheets() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('stonks_backtest_sheets')
    .select('id, name, updated_at')
    .order('updated_at', { ascending: false });
  if (error) { console.error(error); return []; }
  return data || [];
}

export async function createBacktestSheet(name = 'New Backtest') {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('stonks_backtest_sheets')
    .insert({ name, session_id: getSessionId() })
    .select()
    .single();
  if (error) { console.error(error); return null; }
  return data;
}

export async function getBacktestSheet(id) {
  if (!supabase || !id || id === 'local') return null;
  const { data, error } = await supabase
    .from('stonks_backtest_sheets')
    .select('id, name, updated_at')
    .eq('id', id)
    .single();
  if (error) { console.error(error); return null; }
  return data;
}

export async function renameBacktestSheet(id, name) {
  if (!supabase) return;
  await supabase
    .from('stonks_backtest_sheets')
    .update({ name, updated_at: new Date().toISOString() })
    .eq('id', id);
}

export async function deleteBacktestSheet(id) {
  if (!supabase) return;
  await supabase.from('stonks_backtest_sheets').delete().eq('id', id);
}

export async function getBacktestRows(sheetId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('stonks_backtest_entries')
    .select('*')
    .eq('sheet_id', sheetId)
    .order('position_order', { ascending: true });
  if (error) { console.error(error); return []; }
  return data || [];
}

export async function saveBacktestRows(sheetId, rows) {
  if (!supabase) return;
  await supabase.from('stonks_backtest_entries').delete().eq('sheet_id', sheetId);
  if (rows.length === 0) return;
  const toInsert = rows.map((r, i) => ({
    sheet_id: sheetId,
    ticker: (r.ticker || '').toUpperCase().trim(),
    type: r.type || 'purchase',
    date: r.date || null,
    amount: parseFloat(r.amount) || null,
    shares: parseFloat(r.shares) || null,
    position_order: i,
  }));
  const { error } = await supabase.from('stonks_backtest_entries').insert(toInsert);
  if (error) console.error(error);
  await supabase
    .from('stonks_backtest_sheets')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', sheetId);
}
